import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast, Toaster } from 'sonner';
import NotesComposer from '@/components/notes/notes-composer';
import NotesDropOverlay from '@/components/notes/notes-drop-overlay';
import NotesEmptyState from '@/components/notes/notes-empty-state';
import NotesStatusBar from '@/components/notes/notes-status-bar';
import NotesStream from '@/components/notes/notes-stream';
import NotesTitlebar from '@/components/notes/notes-titlebar';
import { Input } from '@/components/ui/input';
import { AUTH_EXPIRED_EVENT } from '@/lib/api/client';
import {
  clearAuthSession,
  getAuthToken,
  setAuthSession,
  subscribeAuthStateChange,
} from '@/lib/api/token-store';
import { imageFilesFromClipboardData } from '@/lib/clipboard/image-clipboard';
import { countOpenTodos, filterBlocks, formatNotesExport } from '@/lib/notes/notes-helpers';
import {
  clearCompletedTodosAction,
  createNoteBlockAction,
  createTodosBlockAction,
  deleteBlockAction,
  getUserNotesState,
  refreshUserNotes,
  subscribeUserNotes,
  toggleTodoItemAction,
  uploadNoteFileAction,
} from '@/lib/stores/user-notes-store';
import { appDayKey } from '@/lib/timezone';

/**
 * Root of the My Notes window (?window=notes). Runs WITHOUT the app shell's bridges on
 * purpose: no realtime bridge (a second WebSocket), no workspace/devtools-lock/idle-lock/
 * face registrations — the main window owns all of those. Auth, theme and change pings
 * arrive over IPC relays instead (pull-first boot state + pushes; see main.js).
 */
function NotesApp() {
  const [theme, setTheme] = useState(() =>
    document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );
  const [pinned, setPinned] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [hasSession, setHasSession] = useState(() => Boolean(getAuthToken()));
  const [notesState, setNotesState] = useState(() => getUserNotesState());
  const composerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const bootedRef = useRef(false);

  const applyTheme = useCallback((nextTheme) => {
    const normalized = nextTheme === 'dark' ? 'dark' : 'light';
    // Class only — the MAIN window owns the localStorage device copy and the dock icon.
    document.documentElement.classList.toggle('dark', normalized === 'dark');
    setTheme(normalized);
  }, []);

  // Boot: pull the auth/theme/pin snapshot from main (immune to push-vs-listener races;
  // idempotent + ref-guarded for StrictMode's double-run).
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    void (async () => {
      const boot = await window.electronAPI?.notesGetBootState?.();
      if (!boot) return;
      if (typeof boot.theme === 'string') applyTheme(boot.theme);
      if (typeof boot.alwaysOnTop === 'boolean') setPinned(boot.alwaysOnTop);
      if (boot.authSession?.token) {
        setAuthSession(boot.authSession);
      }
    })();
  }, [applyTheme]);

  // IPC relays from the main process.
  useEffect(() => {
    const unsubscribe = window.electronAPI?.onNotesAuthChanged?.((session) => {
      if (session?.token) setAuthSession(session);
      else clearAuthSession();
    });
    return () => unsubscribe?.();
  }, []);
  useEffect(() => {
    const unsubscribe = window.electronAPI?.onNotesThemeChanged?.((nextTheme) => applyTheme(nextTheme));
    return () => unsubscribe?.();
  }, [applyTheme]);
  useEffect(() => {
    const unsubscribe = window.electronAPI?.onNotesRealtimePing?.(() => void refreshUserNotes());
    return () => unsubscribe?.();
  }, []);
  useEffect(() => {
    const unsubscribe = window.electronAPI?.onNotesVisibilityChanged?.((detail) => {
      if (detail?.visible) {
        void refreshUserNotes();
        composerRef.current?.focus();
      }
    });
    return () => unsubscribe?.();
  }, []);

  // Store + auth subscriptions (this window's own instances).
  useEffect(() => subscribeUserNotes(() => setNotesState(getUserNotesState())), []);
  useEffect(
    () =>
      subscribeAuthStateChange(() => {
        setHasSession(Boolean(getAuthToken()));
        setNotesState(getUserNotesState());
      }),
    [],
  );

  // 401s in THIS window: clear the local session copy quietly (the main window owns user
  // messaging and will push a fresh session after recovery). The requestToken guard keeps
  // a stale 401 from the previous profile's in-flight request from clearing a new session.
  useEffect(() => {
    const handler = (event) => {
      const requestToken = String(event?.detail?.requestToken || '');
      const active = String(getAuthToken() || '');
      if (!requestToken || !active || requestToken === active) {
        clearAuthSession();
      }
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, handler);
  }, []);

  // Esc guard chain: open Radix layer wins → cancel checklist entry → close search → hide.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (
        document.querySelector('[role="dialog"][data-state="open"]') ||
        document.querySelector('[data-radix-popper-content-wrapper]')
      ) {
        return;
      }
      if (composerRef.current?.cancelTodoMode?.()) {
        event.preventDefault();
        return;
      }
      if (searchOpen) {
        event.preventDefault();
        setSearchOpen(false);
        setSearchText('');
        composerRef.current?.focus();
        return;
      }
      event.preventDefault();
      window.electronAPI?.notesHideWindow?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [searchOpen]);

  const handleFiles = useCallback((files) => {
    for (const file of Array.from(files || [])) {
      void uploadNoteFileAction(file).then((result) => {
        if (result.ok) return;
        if (result.reason === 'too-large') {
          toast.error(`"${file.name}" is larger than 25 MB`);
        } else if (result.error) {
          toast.error(`Couldn't upload "${file.name}"`);
        }
      });
    }
  }, []);

  // Window-level drag & drop with a depth counter (child enter/leave pairs would flicker
  // the overlay otherwise). Only real file drags count.
  const hasFileDrag = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');
  const handleDragEnter = (event) => {
    if (!hasFileDrag(event) || !hasSession) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  };
  const handleDragOver = (event) => {
    if (!hasFileDrag(event) || !hasSession) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };
  const handleDragLeave = (event) => {
    if (!hasFileDrag(event)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragOver(false);
  };
  const handleDrop = (event) => {
    if (!hasFileDrag(event)) return;
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    if (!hasSession) return;
    handleFiles(event.dataTransfer?.files);
  };

  const handlePasteFiles = useCallback(
    (event) => {
      const images = imageFilesFromClipboardData(event.clipboardData);
      if (images.length > 0) {
        event.preventDefault();
        handleFiles(images);
      }
    },
    [handleFiles],
  );

  const handleTogglePin = async () => {
    const applied = await window.electronAPI?.notesSetAlwaysOnTop?.(!pinned);
    if (typeof applied === 'boolean') setPinned(applied);
  };

  const handleToggleSearch = () => {
    setSearchOpen((open) => {
      if (open) {
        setSearchText('');
        composerRef.current?.focus();
        return false;
      }
      return true;
    });
  };
  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  const handleSaveNote = (text) => {
    void createNoteBlockAction(text).then((result) => {
      if (!result.ok) toast.error("Couldn't save the note");
    });
  };
  const handleSaveTodos = (items) => {
    void createTodosBlockAction(items).then((result) => {
      if (!result.ok) toast.error("Couldn't save the checklist");
    });
  };
  const handleToggleItem = (blockId, itemId, done) => {
    void toggleTodoItemAction(blockId, itemId, done).then((result) => {
      if (!result.ok && result.error) toast.error("Couldn't save the change");
    });
  };
  const handleDeleteBlock = (blockId) => {
    void deleteBlockAction(blockId).then((result) => {
      if (!result.ok) toast.error("Couldn't delete");
    });
  };
  const handleClearCompleted = () => {
    void clearCompletedTodosAction().then((result) => {
      if (!result.ok) toast.error("Couldn't clear completed to-dos");
      else if (result.cleared > 0) toast.success('Completed to-dos cleared');
      else toast('No completed to-dos');
    });
  };
  const handleExport = () => {
    const text = formatNotesExport(notesState.blocks || []);
    const blob = new Blob([text || 'No notes yet.'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `my-notes-${appDayKey(new Date())}.txt`;
    anchor.rel = 'noopener';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const blocks = notesState.blocks;
  const filteredBlocks = useMemo(() => filterBlocks(blocks || [], searchText), [blocks, searchText]);
  const openTodoCount = useMemo(() => countOpenTodos(blocks), [blocks]);
  const isEmpty = hasSession && blocks !== null && blocks.length === 0;
  const searching = searchOpen && searchText.trim().length > 0;

  return (
    <div
      className="app-window relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <NotesTitlebar
        pinned={pinned}
        onTogglePin={() => void handleTogglePin()}
        searchOpen={searchOpen}
        onToggleSearch={handleToggleSearch}
        onClearCompleted={handleClearCompleted}
        onExport={handleExport}
      />
      {searchOpen ? (
        <div className="shrink-0 px-3 pt-2">
          <Input
            ref={searchInputRef}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search notes…"
            className="h-7 text-[13px]"
          />
        </div>
      ) : null}
      <NotesComposer
        ref={composerRef}
        disabled={!hasSession}
        onSaveNote={handleSaveNote}
        onSaveTodos={handleSaveTodos}
        onFiles={handleFiles}
        onPasteFiles={handlePasteFiles}
      />
      {isEmpty && !searching ? (
        <NotesEmptyState />
      ) : (
        <NotesStream
          blocks={filteredBlocks}
          onToggleItem={handleToggleItem}
          onDelete={handleDeleteBlock}
          emptyHint={
            searching
              ? 'Nothing matches your search.'
              : hasSession
                ? ''
                : 'Sign in to the main Clarimed window to use your scratchpad.'
          }
        />
      )}
      <NotesStatusBar
        saveState={notesState.saveState}
        hasBlocks={Boolean(blocks && blocks.length > 0)}
        openTodoCount={openTodoCount}
      />
      {dragOver ? <NotesDropOverlay /> : null}
      <Toaster theme={theme} richColors position="bottom-right" closeButton duration={3200} />
    </div>
  );
}

export default NotesApp;
